import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { LayoutDashboard, Package, Settings, LogOut, Tag, Gift, Users, ShoppingBag, Menu, X } from "lucide-react";

export default function AdminLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const [authed, setAuthed] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("prize_admin_token");
    if (!token) { nav("/admin"); setAuthed(false); return; }
    api.get("/admin/me").then(() => setAuthed(true)).catch(() => {
      localStorage.removeItem("prize_admin_token");
      nav("/admin");
      setAuthed(false);
    });
  }, [nav]);

  // Auto-close the drawer whenever the route changes (mobile nav item click).
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  // Lock body scroll while the drawer overlay is open.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const logout = () => {
    localStorage.removeItem("prize_admin_token");
    nav("/admin");
  };

  if (loc.pathname === "/admin") return <Outlet />;
  if (authed === null) return <div className="p-20 text-center text-[#A8A8A8]">Carregando...</div>;
  if (!authed) return null;

  const links = [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/produtos", label: "Produtos", icon: Package },
    { to: "/admin/vendas", label: "Vendas", icon: ShoppingBag },
    { to: "/admin/clientes", label: "Clientes", icon: Users },
    { to: "/admin/cupons", label: "Cupons", icon: Tag },
    { to: "/admin/gift-cards", label: "Gift Cards", icon: Gift },
    { to: "/admin/configuracoes", label: "Configurações", icon: Settings },
  ];

  const sidebar = (
    <aside
      className="w-[280px] md:w-64 h-full bg-white border-r border-[#E8E4DF] p-6 flex flex-col"
      data-testid="admin-sidebar"
    >
      <div className="mb-10">
        <div className="flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L4 9L12 22L20 9L12 2Z" stroke="#1A1A1A" strokeWidth="1" strokeLinejoin="round"/>
            <path d="M4 9H20" stroke="#1A1A1A" strokeWidth="0.7"/>
          </svg>
          <span className="font-display text-base tracking-[0.25em]">PRIZE JOIAS</span>
        </div>
        <p className="overline text-[#A8A8A8] mt-2">Admin</p>
      </div>
      <nav className="space-y-1 flex-1">
        {links.map((l) => {
          const Icon = l.icon;
          const active = loc.pathname.startsWith(l.to);
          return (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`admin-nav-${l.label}`}
              className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors ${active ? "bg-[#FAF9F7] text-[#B8A88A]" : "text-[#1A1A1A] hover:bg-[#FAF9F7]"}`}
            >
              <Icon size={16} /> {l.label}
            </Link>
          );
        })}
      </nav>
      <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-3 px-3 py-2 text-sm text-[#6B6B6B] hover:text-[#1A1A1A]">
        <LogOut size={16} /> Sair
      </button>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F7] md:flex" data-testid="admin-layout">
      {/* Mobile top bar — always visible < md, hidden on desktop. Hosts the
          hamburger/close toggle so the full width is available to content. */}
      <header className="md:hidden sticky top-0 z-40 bg-white border-b border-[#E8E4DF] flex items-center justify-between px-4 h-14">
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
          aria-expanded={mobileOpen}
          className="p-2 -ml-2 text-[#1A1A1A] hover:text-[#B8A88A] transition-colors"
          data-testid="admin-mobile-toggle"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        <div className="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M12 2L4 9L12 22L20 9L12 2Z" stroke="#1A1A1A" strokeWidth="1" strokeLinejoin="round"/>
            <path d="M4 9H20" stroke="#1A1A1A" strokeWidth="0.7"/>
          </svg>
          <span className="font-display text-xs tracking-[0.25em]">PRIZE · Admin</span>
        </div>
        {/* Spacer so the title stays centered visually */}
        <span className="w-6" aria-hidden />
      </header>

      {/* Desktop persistent sidebar */}
      <div className="hidden md:block flex-shrink-0">{sidebar}</div>

      {/* Mobile drawer overlay + sliding sidebar */}
      <div
        onClick={() => setMobileOpen(false)}
        className={`md:hidden fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        data-testid="admin-mobile-overlay"
        aria-hidden={!mobileOpen}
      />
      <div
        className={`md:hidden fixed top-0 left-0 z-50 h-full transition-transform duration-300 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu do administrador"
      >
        {sidebar}
      </div>

      <main className="flex-1 min-w-0 p-4 sm:p-6 md:p-12 overflow-x-auto">
        <Outlet />
      </main>
    </div>
  );
}
